import { useMemo, useRef } from "react";
import { db } from "../../db";
import set from "lodash.set";

import Item from "../item";
import { storedRow } from "../draft/useScopeEdit";
import { doEndpointRefs } from "../endpoint";
import Experiment from "./experiment";
import validate from "./validation";
import { isStarted, referenceOf } from "./links";
import { prepareExperiment } from "./model";
import { createdPagesItems } from "./pages";
import { outcomeItems } from "./outcome";

export function doExperimentRefs(values) {
  const endpoints = [];
  const properties = [];

  // The endpoint of the experiment and its property. The pages are not
  // required: the ones to create don't exist yet, and the endpoint requires
  // them.
  if (values.referenceId) endpoints.push(values.referenceId);
  const property = db.peek("endpoints", values.referenceId)?.propertyId;
  if (property) properties.push(property);

  const array = [];

  if (endpoints.length > 0)
    array.push({
      type: "endpoints",
      items: [...new Set(endpoints)]
    });

  if (properties.length > 0) {
    array.push({
      type: "properties",
      items: [...new Set(properties)]
    });
  }

  set(values, "refs.require", array);

  // Campi che non servono più: stuck in ValKey, cron e metrica di
  // experiments-manager, On End (fuori dall'esperimento vale il vincitore)
  delete values.stuckUser;
  delete values.onEnd;
  delete values.cron;
  delete values.metrics;

  return values;
}

// The stored row of the form: the one of the id the form was opened on (the
// Item's prevItemId, kept in `item` by the form). A new experiment, also the
// copy of another (whose values carry the id and the history of the source),
// has none.
const storedExperiment = (id) => (id ? db.peek("experiments", id) : undefined);

// What a Save stores: the values of the form as prepareExperiment leaves
// them, and the reference of the endpoint (property:endpoint, as builder-block
// looks it up) until the experiment starts: from then on it doesn't change.
const submitter = (item) =>
  function onSubmit(values) {
    const stored = storedExperiment(item.current);

    prepareExperiment(values, stored);
    if (!stored) delete values.id;
    if (!values.outcome && !isStarted(stored) && values.referenceId)
      values.reference =
        referenceOf(db.peek("endpoints", values.referenceId)) ??
        values.reference;

    return doExperimentRefs(values);
  };

// The rows saved in the same request as the experiment (one commit): the
// pages it creates (the block copies of the default page, the endpoint with
// the new variants, the property with the new nodes of its tree), and, when
// a variation is promoted, the endpoint with its new default page. Built on
// the stored rows, never on the drafts.
const extraSaver = (item) =>
  function saveExtra(data) {
    const stored = storedExperiment(item.current);
    const endpoint = storedRow("endpoints", data.referenceId);

    const items = [
      ...createdPagesItems({
        experiment: data,
        endpoint,
        peek: storedRow,
        stored: storedRow,
        doRefs: doEndpointRefs
      }),
      ...outcomeItems({
        data,
        stored,
        endpoint,
        doRefs: doEndpointRefs
      })
    ];

    return { items };
  };

// The title is the label of the experiment in the Analitica records: once
// started, one name for the whole experiment (called by the Item)
const titleLocked = (item) =>
  isStarted(storedExperiment(item?.prevItemId))
    ? "The experiment has started: the title is locked."
    : null;

// Called by the Item with the item as `this`
function onValidate(data) {
  return validate(data, {
    origin: storedExperiment(this.prevItemId),
    endpoint: data.referenceId
      ? db.peek("endpoints", data.referenceId)
      : undefined,
    experiments: db.getTableRef("experiments"),
    blockExists: (id) => db.peek("blocks", id) !== undefined
  });
}

export default function ExperimentsIndex() {
  // The id the form was opened on, written by the form (Experiment)
  const item = useRef();
  const onSubmit = useMemo(() => submitter(item), []);
  const saveExtra = useMemo(() => extraSaver(item), []);

  return (
    <Item
      name="Experiment"
      accessor="experiments"
      linkBack="experiments/list"
      onSubmit={onSubmit}
      onValidate={onValidate}
      saveExtra={saveExtra}
      titleLocked={titleLocked}
      NameAndDescriptionColor="#4d194d"
    >
      <Experiment item={item} />
    </Item>
  );
}
